import { useEffect, useMemo, useState } from "react";
import { useModelRegistry } from "../lib/api/model";
import { useSearchParams } from "react-router-dom";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
} from "@tanstack/react-table";
import {
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { AlertTriangle, CheckCircle2, AlertOctagon } from "lucide-react";

import { PageHeader } from "../components/PageHeader";
import { ReturnChip } from "../components/ReturnChip";
import { StepShell } from "../components/StepShell";
import { Alert, AlertDescription, AlertTitle } from "../../../ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../../../ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../ui/tabs";
import type { Segment } from "../lib/api/types";
import {
  CSI_CRIT,
  CSI_WARN,
  PSI_CRIT,
  PSI_WARN,
  directionLabel,
  statusClasses,
  statusLabel,
  useCsi,
  usePsi,
  type FeatureStabilityRow,
  type InputStabilityResponse,
} from "../lib/api/stability";

type TabKey = "psi" | "csi";


function buildBannerMessage(
  rows: FeatureStabilityRow[],
  aggregateFlag: InputStabilityResponse["aggregateFlag"],
): { tone: "critical" | "warn" | "stable"; text: string } {
  const criticals = rows.filter((r) => r.status === "CRITICAL");
  if (aggregateFlag.fired) {
    return {
      tone: "warn",
      text: `Aggregate flag fired · ${aggregateFlag.warnFeatureCount} features simultaneously in WARN: ${aggregateFlag.featuresInWarn.join(", ")}`,
    };
  }
  if (criticals.length > 0) {
    const list = criticals
      .map((c) => `${c.displayName}${c.value !== null ? ` (${c.value.toFixed(2)})` : ""}`)
      .join(", ");
    return {
      tone: "critical",
      text: `${criticals.length} feature${criticals.length === 1 ? "" : "s"} in CRITICAL: ${list}. Monitored individually no aggregate population-wide instability.`,
    };
  }
  return {
    tone: "stable",
    text: "No instability flags. All monitored features within thresholds.",
  };
}

function BannerRegion({
  rows,
  aggregateFlag,
}: {
  rows: FeatureStabilityRow[];
  aggregateFlag: InputStabilityResponse["aggregateFlag"];
}) {
  const { tone, text } = buildBannerMessage(rows, aggregateFlag);
  if (tone === "critical") {
    return (
      <Alert className="border-destructive/40 bg-[color:var(--destructive)]/10 text-foreground">
        <AlertOctagon className="h-4 w-4 text-destructive" />
        <AlertTitle className="text-destructive">Critical signal</AlertTitle>
        <AlertDescription className="text-foreground/80">{text}</AlertDescription>
      </Alert>
    );
  }
  if (tone === "warn") {
    return (
      <Alert className="border-amber/40 bg-amber-dim text-foreground">
        <AlertTriangle className="h-4 w-4 text-amber" />
        <AlertTitle className="text-amber">Aggregate warning</AlertTitle>
        <AlertDescription className="text-foreground/80">{text}</AlertDescription>
      </Alert>
    );
  }
  return (
    <Alert className="border-[color:var(--approve)]/40 bg-[color:var(--approve)]/10 text-foreground">
      <CheckCircle2 className="h-4 w-4 text-[color:var(--approve)]" />
      <AlertTitle className="text-[color:var(--approve)]">All stable</AlertTitle>
      <AlertDescription className="text-foreground/80">{text}</AlertDescription>
    </Alert>
  );
}

function StatusPill({ status }: { status: FeatureStabilityRow["status"] }) {
  return (
    <span
      className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${statusClasses(status)}`}
    >
      {statusLabel(status)}
    </span>
  );
}

function Sparkline({
  trend,
  warn,
  crit,
}: {
  trend: { cohort: string; value: number | null }[];
  warn: number;
  crit: number;
}) {
  const hasData = trend.some((p) => p.value !== null);
  if (!hasData) {
    return <div className="text-xs text-muted-foreground">—</div>;
  }
  return (
    <div className="h-[34px] w-[120px]">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={trend} margin={{ top: 2, right: 2, left: 2, bottom: 0 }}>
          <XAxis dataKey="cohort" hide />
          <YAxis hide domain={[0, (max: number) => Math.max(max, crit * 1.05)]} />
          <ReferenceLine y={warn} stroke="hsl(var(--muted-foreground))" strokeDasharray="2 2" strokeOpacity={0.4} />
          <ReferenceLine y={crit} stroke="var(--decline)" strokeDasharray="2 2" strokeOpacity={0.5} />
          <Line
            type="monotone"
            dataKey="value"
            stroke="var(--amber)"
            strokeWidth={1.75}
            dot={false}
            connectNulls={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function buildColumns(
  metric: "PSI" | "CSI",
): ColumnDef<FeatureStabilityRow>[] {
  const warn = metric === "PSI" ? PSI_WARN : CSI_WARN;
  const crit = metric === "PSI" ? PSI_CRIT : CSI_CRIT;

  const cols: ColumnDef<FeatureStabilityRow>[] = [
    {
      accessorKey: "feature",
      header: "Feature",
      cell: ({ row }) => (
        // Macro/economic features render in Athena Yellow; all others in white.
        <div className={`font-medium ${row.original.featureType === "Macro Features" ? "text-amber" : "text-foreground"}`}>
          {row.original.displayName}
        </div>
      ),
    },
    {
      accessorKey: "type",
      header: "Type",
      cell: ({ row }) => (
        <span className="text-xs uppercase tracking-wide text-muted-foreground">
          {row.original.type}
        </span>
      ),
    },
    {
      accessorKey: "value",
      header: metric,
      cell: ({ row }) => {
        const v = row.original.value;
        if (v === null) {
          return <span className="text-muted-foreground">—</span>;
        }
        // Macro/economic features render in Athena Yellow; all others in white.
        const isMacro = row.original.featureType === "Macro Features";
        return (
          <span className={`tabular-nums ${isMacro ? "text-amber font-semibold" : "text-foreground"}`}>
            {v.toFixed(4)}
          </span>
        );
      },
    },
    {
      id: "trend",
      header: "Trend",
      cell: ({ row }) => (
        <Sparkline trend={row.original.trend} warn={warn} crit={crit} />
      ),
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => <StatusPill status={row.original.status} />,
    },
  ];

  if (metric === "CSI") {
    cols.push({
      id: "direction",
      header: "Direction",
      cell: ({ row }) => {
        const d = row.original.direction;
        const cls =
          d === "UP"
            ? "text-amber font-medium"
            : d === "DOWN"
              ? "text-[color:var(--approve)] font-medium"
              : "text-muted-foreground";
        return <span className={`text-xs ${cls}`}>{directionLabel(d)}</span>;
      },
    });
  }

  return cols;
}

function StabilityTable({
  data,
  metric,
  onRowClick,
  highlightFeature,
}: {
  data: InputStabilityResponse;
  metric: "PSI" | "CSI";
  onRowClick: (r: FeatureStabilityRow) => void;
  highlightFeature?: string;
}) {
  const columns = useMemo(() => buildColumns(metric), [metric]);
  const sortedRows = useMemo(() => data.rows, [data.rows]);
  const table = useReactTable({
    data: sortedRows,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="rounded-lg border border-border bg-card">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((hg) => (
            <TableRow key={hg.id} className="border-border hover:bg-transparent">
              {hg.headers.map((h) => (
                <TableHead
                  key={h.id}
                  className="text-[11px] uppercase tracking-[0.18em] text-amber"
                >
                  {flexRender(h.column.columnDef.header, h.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => {
            const isHighlight =
              highlightFeature && row.original.featureKey === highlightFeature;
            return (
              <TableRow
                key={row.id}
                onClick={() => onRowClick(row.original)}
                className={`cursor-pointer border-border hover:bg-muted/30 ${isHighlight ? "bg-amber-dim/40" : ""}`}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id} className="align-middle">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

function FeatureDrawer({
  row,
  metric,
  open,
  onOpenChange,
}: {
  row: FeatureStabilityRow | null;
  metric: "PSI" | "CSI";
  open: boolean;
  onOpenChange: (b: boolean) => void;
}) {
  const warn = metric === "PSI" ? PSI_WARN : CSI_WARN;
  const crit = metric === "PSI" ? PSI_CRIT : CSI_CRIT;
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full max-w-xl overflow-y-auto bg-background border-border risk-explorer-theme !z-[9999] [&>button_svg]:text-white">
        {row && (
          <>
            <SheetHeader className="border-b border-border/30 pb-4">
              <SheetTitle className="flex items-center gap-3 text-foreground">
                <span className={row.featureType === "Macro Features" ? "text-amber" : "text-foreground"}>
                  {row.displayName}
                </span>
                <StatusPill status={row.status} />
              </SheetTitle>
              <SheetDescription className="text-foreground/60">
                {metric} trend across monitored cohorts. Dashed lines mark WARN
                ({warn.toFixed(2)}) and CRITICAL ({crit.toFixed(2)}) thresholds.
              </SheetDescription>
            </SheetHeader>
            <div className="mt-6 space-y-6">
              <div className="h-[220px] border border-border bg-card p-3">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={row.trend} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                    <XAxis
                      dataKey="cohort"
                      tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
                      tickLine={false}
                      axisLine={{ stroke: "hsl(var(--border))" }}
                    />
                    <YAxis
                      tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
                      tickLine={false}
                      axisLine={{ stroke: "hsl(var(--border))" }}
                      domain={[0, (max: number) => Math.max(max, crit * 1.2)]}
                    />
                    <Tooltip
                      contentStyle={{
                        background: "hsl(var(--popover))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: 6,
                        fontSize: 11,
                      }}
                      formatter={(v: number) => [v.toFixed(4), metric]}
                    />
                    <ReferenceLine y={warn} stroke="var(--amber)" strokeDasharray="3 3" strokeOpacity={0.7} />
                    <ReferenceLine y={crit} stroke="var(--decline)" strokeDasharray="3 3" strokeOpacity={0.7} />
                    <Line
                      type="monotone"
                      dataKey="value"
                      stroke="var(--amber)"
                      strokeWidth={2}
                      dot={false}
                      connectNulls={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="border border-border bg-card p-3">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-amber">
                    Training baseline
                  </div>
                  <div className="mt-1 text-sm text-foreground">
                    {row.type === "categorical"
                      ? "Mix from 2018–2023 training period"
                      : "Mean / variance from 2018–2023 training period"}
                  </div>
                </div>
                <div className="border border-border bg-card p-3">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-amber">
                    Current cohort
                  </div>
                  <div className="mt-1 text-sm text-foreground tabular-nums">
                    {metric}{" "}
                    {row.value === null ? "—" : row.value.toFixed(4)}
                  </div>
                </div>
              </div>
              {metric === "CSI" && row.direction && (
                <div className="border border-border bg-card p-3 text-sm">
                  <span className="text-[10px] uppercase tracking-[0.18em] text-amber">
                    Direction
                  </span>
                  <div className="mt-1 text-foreground">
                    {directionLabel(row.direction)}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function TabBody({
  metric,
  segment,
  query,
  autoOpenFeature,
}: {
  metric: "PSI" | "CSI";
  segment: Segment;
  query: ReturnType<typeof usePsi>;
  autoOpenFeature?: string;
}) {
  const [openRow, setOpenRow] = useState<FeatureStabilityRow | null>(null);
  const [open, setOpen] = useState(false);
  const data = query.data;

  useEffect(() => {
    if (!autoOpenFeature || !data) return;
    const match = data.rows.find((r) => r.featureKey === autoOpenFeature);
    if (match) {
      setOpenRow(match);
      setOpen(true);
    }
  }, [autoOpenFeature, data]);

  if (query.isLoading || !data) {
    return <div className="h-48 animate-pulse rounded-md bg-muted/20" />;
  }

  return (
    <div className="space-y-4">
      <BannerRegion rows={data.rows} aggregateFlag={data.aggregateFlag} />

      <div className="flex items-baseline justify-between">
        <div>
          <div className="text-sm font-semibold text-foreground">
            {metric} · {segment}
          </div>
          <div className="text-xs text-muted-foreground">{data.cohortRange}</div>
        </div>
        <div className="text-[11px] uppercase tracking-[0.18em] text-amber">
          {data.rows.filter((r) => r.status !== "SKIPPED").length} features monitored
        </div>
      </div>

      <StabilityTable
        data={data}
        metric={metric}
        onRowClick={(r) => {
          setOpenRow(r);
          setOpen(true);
        }}
        highlightFeature={autoOpenFeature}
      />

      <ul className="space-y-1 text-xs text-muted-foreground">
        {data.notes.map((n, i) => (
          <li key={i}>· {n}</li>
        ))}
      </ul>

      <FeatureDrawer
        row={openRow}
        metric={metric}
        open={open}
        onOpenChange={setOpen}
      />
    </div>
  );
}

export function InputStabilityPage() {
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const segmentParam = searchParams.get("segment");
  const featureParam = searchParams.get("feature") ?? undefined;
  const fromParam = searchParams.get("from");

  const [segment, setSegment] = useState<Segment>(
    (segmentParam === "PG" || segmentParam === "CORP" || segmentParam === "OVERALL"
      ? segmentParam
      : "OVERALL") as Segment,
  );
  const [tab, setTab] = useState<TabKey>(
    tabParam === "csi" ? "csi" : "psi",
  );

  const { data: models = [] } = useModelRegistry();
  const [selectedModelId, setSelectedModelId] = useState<number>(1);

  const uniqueModels = models.filter(
    (m, i, arr) =>
      arr.findIndex(
        (x) => x.modelName === m.modelName && x.modelVersion === m.modelVersion,
      ) === i,
  );

  const psi = usePsi(segment, selectedModelId);
  const csi = useCsi(segment, selectedModelId);

  return (
    <StepShell step={2} modelId={selectedModelId} segment={segment} models={uniqueModels} onModelChange={setSelectedModelId}>
      <PageHeader
        title="Input Stability"
        subtitle="PSI tells you whether the population mix has shifted. CSI tells you whether each feature's relationship to default has changed."
      />

      {fromParam === "health" && (
        <div className="mt-4">
          <ReturnChip />
        </div>
      )}

      <Tabs
        value={tab}
        onValueChange={(v) => setTab(v as TabKey)}
        className="mt-6 w-full"
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <TabsList>
            <TabsTrigger value="psi">PSI</TabsTrigger>
            <TabsTrigger value="csi">CSI</TabsTrigger>
          </TabsList>
          <div className="flex items-center gap-2">
            <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
              Segment
            </span>
            <Select value={segment} onValueChange={(v) => setSegment(v as Segment)}>
              <SelectTrigger className="h-8 w-[140px] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="OVERALL">Overall</SelectItem>
                <SelectItem value="PG">PG</SelectItem>
                <SelectItem value="CORP">Corp</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <TabsContent value="psi" className="mt-4">
          <TabBody
            metric="PSI"
            segment={segment}
            query={psi}
            autoOpenFeature={tab === "psi" ? featureParam : undefined}
          />
        </TabsContent>
        <TabsContent value="csi" className="mt-4">
          <TabBody
            metric="CSI"
            segment={segment}
            query={csi}
            autoOpenFeature={tab === "csi" ? featureParam : undefined}
          />
        </TabsContent>
      </Tabs>
    </StepShell>
  );
}
